import React, { Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import Layout from '../pages/Layouts/Authorized';
import { SignIn } from '../pages/SignIn';
import RouteWrapper from './route';
import { defaultRoutes } from './routes';
import { useAuth } from "../hooks/auth";
const NotFound = React.lazy(() => import('../pages/Layouts/Authorized/notFound'));
const NoAccess = React.lazy(() => import('../pages/NoAccess/index.jsx'));

// Componente responsável por rolar a página para o topo sempre que o caminho mudar
const ScrollToTop = ({ children }) => {
    const location = useLocation();

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [location.pathname]);

    return children;
};

// Componente que aplica o Layout e o Suspense com fallback para uma tela de loading
const LayoutWithSuspense = ({ children }) => (
    <Layout>
        <ScrollToTop>
            <Suspense fallback={<Loading />}>{children}</Suspense>
        </ScrollToTop>
    </Layout>
);

const RootRedirect = () => {
    const { user, hasGestao, hasStudio, canAccessAcademy } = useAuth();
    if (!user) return <Navigate to="/signIn" replace />;
    if (hasGestao) return <Navigate to="/reports?model=cashflow" replace />;
    if (hasStudio) return <Navigate to="/reports/advanced?tab=reports" replace />;
    if (typeof canAccessAcademy === "function" && canAccessAcademy()) {
        return <Navigate to="/academy" replace />;
    }
    return <Navigate to="/no-access" replace />;
};

// Função para gerar as rotas dinamicamente
const generateRoutes = (routes) => {
    const walk = (items) => {
        if (!Array.isArray(items)) return [];
        return items.flatMap((route) => {
            if (route?.type === "divider") return [];
            if (route?.isMenuOnly) return walk(route.children || []);
            if (!route?.key || !route?.element) return walk(route?.children || []);

            const requiresAuth = route.roles && route.roles.length > 0;
            const entry = {
                path: route.key,
                element: requiresAuth ? (
                    <LayoutWithSuspense>
                        <RouteWrapper
                            element={route.element}
                            auth
                            roles={route.roles}
                            requiredModules={route.requiredModules}
                        />
                    </LayoutWithSuspense>
                ) : (
                    <RouteWrapper
                        element={route.element}
                        roles={route.roles}
                        requiredModules={route.requiredModules}
                    />
                ),
            };

            return [entry, ...walk(route.children || [])];
        });
    };

    return walk(routes);
};

// Definição das rotas do aplicativo
const RoutesList = () => {
    const dynamicRoutes = generateRoutes(defaultRoutes);

    // Importar o ReportViewer para rota pública independente

    const router = createBrowserRouter([
        {
            path: "/",
            element: <RootRedirect />
        },
        {
            path: "/signIn",
            element: <RouteWrapper element={SignIn} />
        },
        {
            path: "/no-access",
            element: (
                <LayoutWithSuspense>
                    <RouteWrapper element={NoAccess} auth />
                </LayoutWithSuspense>
            )
        },
        // Rota pública independente para visualização de relatórios
        
        ...dynamicRoutes, // Adiciona as rotas dinâmicas geradas
        {
            path: "*",
            element: (
                <LayoutWithSuspense>
                    <RouteWrapper element={NotFound} />
                </LayoutWithSuspense>
            )
        },
    ]);

    return (
        <RouterProvider router={router} />
    );
};

export default RoutesList;
